import { useState } from "react";
import LabelledComboBox from "../../atoms/LabelledComboBox/LabelledComboBox";
import Spinner from "../../atoms/Spinner/Spinner";
import { Visibility, IFriendData, IFriendPermission, ListPermission } from "../../../services/GiftsApi";
import InfoAlert from "../../atoms/InfoAlert/InfoAlert";
import FriendListPermission from "../FriendListPermission/FriendListPermission";

interface ListVisibilitySelectorProps {
    visibility: Visibility;
    availableFriends: IFriendData[];
    selectedFriends: IFriendPermission[];
    onChange: (visibility: Visibility, friends: IFriendPermission[]) => void;
}

const ListVisibilitySelector = (props: ListVisibilitySelectorProps) => {

    const [visibility, setVisibility] = useState<Visibility>(props.visibility);

    const [selectedFriends, setSelectedFriends] = useState<IFriendPermission[]>(props.selectedFriends);

    const handleVisibilityChanged = (visibility: Visibility) => {
        setVisibility(visibility);
        props.onChange(visibility, selectedFriends);
    }

    const handleFriendChanged = (userName: string, permission: ListPermission) => {
        let newPermissions = [...selectedFriends];
        const existingPermission = newPermissions.find(f => f.userName === userName);
        if (existingPermission) {
            if (permission === 'none') {
                newPermissions = newPermissions.filter(f => f.userName !== userName);
            } else {
                existingPermission.permission = permission;
            }
        } else if (permission !== 'none') {
            newPermissions.push({ userName, permission });
        }

        setSelectedFriends(newPermissions);
        props.onChange(visibility, newPermissions);
    }

    return (
        <>
            <LabelledComboBox name="visibility" label="Visibility:" value={visibility} onChange={e => handleVisibilityChanged(e.target.value as Visibility)} options={[
                { value: "friends", label: "All of my friends" },
                { value: "specific", label: "Specific friends" },
                { value: "private", label: "Private" }
            ]} />

            {visibility === "friends" &&
                <InfoAlert title="List visible to all friends" message="All of your friends will be able to see this list. They will also all be able to see purchases made by others." />
            }

            {visibility === "specific" &&
                <>
                    <InfoAlert title="List visible to specific friends" message="Only the friends you select below will be able to see this list. They will also be able to see purchases made by others." />
                    {props.availableFriends === null
                        ? <Spinner />
                        : props.availableFriends.map(f =>
                            <FriendListPermission
                                key={f.id}
                                id={f.id}
                                userName={f.userName}
                                firstName={f.firstName}
                                lastName={f.lastName}
                                permission={selectedFriends.find(friend => friend.userName === f.userName)?.permission ?? 'none'}
                                onChange={permission => handleFriendChanged(f.userName, permission)} />
                        )
                    }
                </>
            }

            {visibility === "private" &&
                <InfoAlert title="List visible to no one" message="Only you will be able to see this list." />
            }
        </>
    );
}

export default ListVisibilitySelector;